import { useCallback, useEffect, useRef } from 'react';
import { getSettingsScrollRoot, getSettingsSectionElement } from './settingsNavigation';

const REDUCED_MOTION_UNLOCK_MS = 50;
const SMOOTH_SCROLL_UNLOCK_MS = 600;

const useSettingsSectionScroll = () => {
    const isScrollingRef = useRef(false);
    const unlockTimeoutRef = useRef(null);
    const scrollTargetRef = useRef(null);

    const unlockScroll = useCallback(() => {
        isScrollingRef.current = false;
        window.clearTimeout(unlockTimeoutRef.current);
        unlockTimeoutRef.current = null;
        scrollTargetRef.current?.removeEventListener('scrollend', unlockScroll);
        scrollTargetRef.current = null;
    }, []);

    useEffect(() => unlockScroll, [unlockScroll]);

    const scrollToSection = useCallback(
        (id, { behavior = 'smooth' } = {}) => {
            const element = getSettingsSectionElement(id);
            if (!element) return false;

            const prefersReducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
            const useAuto = prefersReducedMotion || behavior === 'auto';

            unlockScroll();
            isScrollingRef.current = true;
            const scrollTarget = getSettingsScrollRoot() || window;
            scrollTargetRef.current = scrollTarget;
            scrollTarget.addEventListener('scrollend', unlockScroll, { once: true });
            window.clearTimeout(unlockTimeoutRef.current);
            unlockTimeoutRef.current = window.setTimeout(
                unlockScroll,
                useAuto ? REDUCED_MOTION_UNLOCK_MS : SMOOTH_SCROLL_UNLOCK_MS
            );

            element.scrollIntoView({ behavior: useAuto ? 'auto' : behavior, block: 'start' });
            return true;
        },
        [unlockScroll]
    );

    return { isScrollingRef, scrollToSection };
};

export default useSettingsSectionScroll;
